import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ModalOverlayCoordinationService } from "./modal-overlay-coordination.service";
import {
  AlertController,
  AlertOptions,
  ModalController,
  ModalOptions,
} from "@ionic/angular";

/** Plus la valeur est basse, plus la modale est prioritaire. */
export const ModalQueuePriority = {
  CRITICAL: 0,
  HIGH: 100,
  NORMAL: 200,
  NORMAL_LOW: 250,
  LOW: 300,
} as const;

export type ModalQueuePriorityValue =
  (typeof ModalQueuePriority)[keyof typeof ModalQueuePriority];

export interface ModalDismissResult<T = unknown> {
  data?: T;
  role?: string;
}

export interface ModalQueueEnqueueOptions {
  /** Identifiant stable pour déduplication (ex. `location-disclosure`). */
  id: string;
  priority?: ModalQueuePriorityValue;
  /** Ignore les doublons déjà en file ou en cours pour le même `id`. */
  dedupe?: boolean;
  /** Attendre la fin de l’écran `/splash` avant d’afficher (défaut: true). */
  waitForSplashEnd?: boolean;
  /** Présenter même si une autre modale Ionic est au-dessus (modales de complétion compte). */
  forcePresent?: boolean;
  /** Prédicat optionnel exécuté avant chaque tentative d’affichage. */
  when?: () => boolean | Promise<boolean>;
}

interface QueueJob {
  id: string;
  priority: number;
  enqueuedAt: number;
  waitForSplashEnd: boolean;
  forcePresent: boolean;
  when?: () => boolean | Promise<boolean>;
  run: () => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

const SPLASH_ROUTE_FRAGMENT = "/splash";
const PRESENT_POLL_MS = 150;
const PRESENT_MAX_WAIT_MS = 120_000;

@Injectable({ providedIn: "root" })
export class ModalQueueService {
  private readonly queue: QueueJob[] = [];
  private draining = false;
  private presenting = false;
  private currentJobId: string | null = null;
  private readonly pendingById = new Map<string, Promise<unknown>>();

  constructor(
    private modalController: ModalController,
    private alertController: AlertController,
    private router: Router,
    private modalOverlayCoordination: ModalOverlayCoordinationService
  ) {}

  isPresenting(): boolean {
    return this.presenting;
  }

  getCurrentJobId(): string | null {
    return this.currentJobId;
  }

  hasPendingJobs(): boolean {
    return this.queue.length > 0 || this.presenting;
  }

  /**
   * Présente une modale Ionic via la file (une seule à la fois).
   */
  enqueueModal<TData = unknown>(
    options: ModalOptions,
    config: ModalQueueEnqueueOptions
  ): Promise<ModalDismissResult<TData> | undefined> {
    return this.enqueue(async () => {
      const modal = await this.modalController.create(options);
      await modal.present();
      return (await modal.onDidDismiss()) as ModalDismissResult<TData>;
    }, config) as Promise<ModalDismissResult<TData> | undefined>;
  }

  /**
   * Présente une alerte Ionic via la file.
   */
  enqueueAlert<TResult = unknown>(
    options: AlertOptions,
    config: ModalQueueEnqueueOptions
  ): Promise<TResult | undefined> {
    return this.enqueue(async () => {
      const alert = await this.alertController.create(options);
      await alert.present();
      const result = await alert.onDidDismiss();
      return result as TResult;
    }, config) as Promise<TResult | undefined>;
  }

  private enqueue(
    run: () => Promise<unknown>,
    config: ModalQueueEnqueueOptions
  ): Promise<unknown> {
    const dedupe = config.dedupe !== false;
    if (dedupe) {
      const existing = this.pendingById.get(config.id);
      if (existing) {
        return existing;
      }
    }

    const promise = new Promise<unknown>((resolve, reject) => {
      const job: QueueJob = {
        id: config.id,
        priority: config.priority ?? ModalQueuePriority.NORMAL,
        enqueuedAt: Date.now(),
        waitForSplashEnd: config.waitForSplashEnd !== false,
        forcePresent: config.forcePresent === true,
        when: config.when,
        run,
        resolve,
        reject,
      };
      this.queue.push(job);
      this.sortQueue();
      void this.drain();
    });

    if (dedupe) {
      // Must be set after `promise` is created — referencing it inside the
      // executor causes "Cannot access before initialization" (TDZ).
      this.pendingById.set(config.id, promise);
      promise.finally(() => this.pendingById.delete(config.id));
    }

    return promise;
  }

  private sortQueue(): void {
    this.queue.sort(
      (a, b) =>
        a.priority - b.priority || a.enqueuedAt - b.enqueuedAt
    );
  }

  private async drain(): Promise<void> {
    if (this.draining) {
      return;
    }
    this.draining = true;
    try {
      while (this.queue.length > 0) {
        this.sortQueue();
        const job = this.queue.shift();
        if (!job) {
          break;
        }
        this.currentJobId = job.id;
        this.presenting = true;
        try {
          await this.waitUntilCanPresent(job);
          if (job.when && !(await job.when())) {
            job.resolve(undefined);
            continue;
          }
          const result = await job.run();
          job.resolve(result);
        } catch (error) {
          console.warn(`[ModalQueue] Job "${job.id}" failed:`, error);
          job.reject(error);
        } finally {
          this.presenting = false;
          this.currentJobId = null;
        }
      }
    } finally {
      this.draining = false;
    }
  }

  private async waitUntilCanPresent(job: QueueJob): Promise<void> {
    const started = Date.now();
    let lastBlockedBy = "";
    while (Date.now() - started < PRESENT_MAX_WAIT_MS) {
      const onSplash = this.isOnSplashRoute();
      if (job.waitForSplashEnd && onSplash) {
        if (lastBlockedBy !== "splash") {
          console.log(`[ModalQueue] "${job.id}" waiting — on splash route`);
          lastBlockedBy = "splash";
        }
        await this.delay(PRESENT_POLL_MS);
        continue;
      }
      if (job.when && !(await job.when())) {
        if (lastBlockedBy !== "when") {
          console.log(`[ModalQueue] "${job.id}" waiting — "when" predicate false`);
          lastBlockedBy = "when";
        }
        await this.delay(PRESENT_POLL_MS);
        continue;
      }
      if (this.modalOverlayCoordination.hasOpenOverlay()) {
        if (lastBlockedBy !== "overlay-modal") {
          console.log(`[ModalQueue] "${job.id}" waiting — template overlay open`);
          lastBlockedBy = "overlay-modal";
        }
        await this.delay(PRESENT_POLL_MS);
        continue;
      }
      if (!job.forcePresent) {
        const modalTop = await this.modalController.getTop();
        if (modalTop) {
          const blockKey = `modal-${(modalTop as any).id ?? "unknown"}`;
          if (lastBlockedBy !== blockKey) {
            console.log(`[ModalQueue] "${job.id}" waiting — modal on top: id=${(modalTop as any).id ?? "?"}`);
            lastBlockedBy = blockKey;
          }
          await this.delay(PRESENT_POLL_MS);
          continue;
        }
      }
      try {
        const alertTop = await this.alertController.getTop();
        if (alertTop) {
          if (lastBlockedBy !== "alert") {
            console.log(`[ModalQueue] "${job.id}" waiting — alert on top`);
            lastBlockedBy = "alert";
          }
          await this.delay(PRESENT_POLL_MS);
          continue;
        }
      } catch {
        // getTop() indisponible sur certaines versions — ignorer.
      }
      if (lastBlockedBy) {
        console.log(`[ModalQueue] "${job.id}" — proceeding to present`);
      }
      return;
    }
    console.warn(
      `[ModalQueue] Timeout waiting to present "${job.id}" — proceeding anyway.`
    );
  }

  private isOnSplashRoute(): boolean {
    const url = this.router.url || "";
    return url.includes(SPLASH_ROUTE_FRAGMENT);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
